/**
 * Phase 7-B — Operator-side listings table.
 *
 * Surfaces:
 *  - GET /operators/me/listings (`02_api_spec.md §4.1`)
 *  - POST /marketplace/listings, PUT /marketplace/listings/{id} (CAS via ifVersion)
 *  - POST /marketplace/listings/{id}/pause | /resume
 *  - DELETE /marketplace/listings/{id}
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../../api/client';
import Modal from '../../components/Modal';
import FormField from '../../components/FormField';
import Badge from '../../components/Badge';
import {
  weiToAdi,
  adiToWei,
  listingStatusBadge,
  uuid,
} from './utils';
import type {
  ListingsPage,
  MarketplaceListing,
  PricingModel,
} from '@infinia/ida-sdk';
import { encodePathSegment } from '../../lib/url';

interface AgentOption { did: string; name: string; }
interface PolicyOption { id: string; name: string; }
interface ToolOption { id: string; name: string; }

type ModalKind = 'create' | 'edit' | 'pause' | 'delete' | null;

interface ListingForm {
  agentDid: string;
  title: string;
  description: string;
  capabilityScope: string;
  pricingModel: PricingModel;
  priceAdi: string;
  ibctPolicyId: string;
  mcpToolId: string;
  latencyP95Ms: string;
  uptimePct: string;
}

const emptyForm: ListingForm = {
  agentDid: '',
  title: '',
  description: '',
  capabilityScope: '',
  pricingModel: 'per_call',
  priceAdi: '',
  ibctPolicyId: '',
  mcpToolId: '',
  latencyP95Ms: '',
  uptimePct: '',
};

export default function MyListings() {
  const { t } = useTranslation(['marketplace', 'common']);
  const [items, setItems] = useState<MarketplaceListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; msg: string } | null>(null);

  const [modal, setModal] = useState<ModalKind>(null);
  const [editing, setEditing] = useState<MarketplaceListing | null>(null);
  const [form, setForm] = useState<ListingForm>(emptyForm);
  const [pauseReason, setPauseReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [agentOptions, setAgentOptions] = useState<AgentOption[]>([]);
  const [policyOptions, setPolicyOptions] = useState<PolicyOption[]>([]);
  const [toolOptions, setToolOptions] = useState<ToolOption[]>([]);
  const [toolsLoading, setToolsLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<ListingsPage>('/operators/me/listings?limit=100');
      setItems(res.items ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('myListings.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { void refresh(); }, [refresh]);

  const fetchFormOptions = useCallback(() => {
    apiFetch<{ items?: Array<{ id: string; document?: { modelInfo?: { name?: string } }; operator?: string }> }>('/agents/me?limit=100')
      .then((res) => {
        const opts = (res.items ?? []).map((a) => ({
          did: a.id,
          name: a.document?.modelInfo?.name || a.id.split(':').pop()?.slice(0, 16) || a.id,
        }));
        setAgentOptions(opts);
      })
      .catch(() => {});
    apiFetch<{ items?: Array<{ id: string; name?: string }> }>('/operators/me/policies?limit=100')
      .then((res) => {
        const opts = (res.items ?? []).map((p) => ({ id: p.id, name: p.name || p.id }));
        setPolicyOptions(opts);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { fetchFormOptions(); }, [fetchFormOptions]);

  useEffect(() => {
    if (!form.agentDid) { setToolOptions([]); return; }
    setToolsLoading(true);
    apiFetch<{ items?: Array<{ id: string; name: string }> }>(`/agents/${encodePathSegment(form.agentDid)}/tools`)
      .then((res) => {
        setToolOptions((res.items ?? []).map((t) => ({ id: t.id, name: t.name })));
      })
      .catch(() => setToolOptions([]))
      .finally(() => setToolsLoading(false));
  }, [form.agentDid]);

  useEffect(() => {
    if (!toast) return;
    const h = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(h);
  }, [toast]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setToolOptions([]);
    fetchFormOptions();
    setModal('create');
  };

  const openEdit = (l: MarketplaceListing) => {
    setEditing(l);
    setForm({
      agentDid: l.agentDid,
      title: l.title,
      description: l.description ?? '',
      capabilityScope: l.capabilityScope,
      pricingModel: l.pricing.model,
      priceAdi: weiToAdi(l.pricing.priceWei),
      ibctPolicyId: l.ibctPolicyId ?? '',
      mcpToolId: l.mcpToolId ?? '',
      latencyP95Ms: l.sla?.latencyP95Ms ? String(l.sla.latencyP95Ms) : '',
      uptimePct: l.sla?.uptimePct ? String(l.sla.uptimePct) : '',
    });
    setFormError(null);
    setModal('edit');
  };

  const openPause = (l: MarketplaceListing) => {
    setEditing(l);
    setPauseReason('manual_pause');
    setFormError(null);
    setModal('pause');
  };

  const openDelete = (l: MarketplaceListing) => {
    setEditing(l);
    setFormError(null);
    setModal('delete');
  };

  const close = () => {
    setModal(null);
    setEditing(null);
    setForm(emptyForm);
    setPauseReason('');
    setFormError(null);
  };

  const submitForm = async () => {
    setFormError(null);
    // The API requires a policy for a new listing. Sending none used to come
    // back as a 500 INTERNAL_ERROR with no hint of which field was missing.
    if (modal === 'create' && !form.ibctPolicyId) {
      setFormError(t('myListings.form.policyRequired', {
        defaultValue: 'Choose the IBCT policy this listing is sold under. If you have none, create one under Policies first.',
      }));
      return;
    }
    setSubmitting(true);
    try {
      const sla =
        form.latencyP95Ms || form.uptimePct
          ? {
              latencyP95Ms: form.latencyP95Ms ? Number(form.latencyP95Ms) : undefined,
              uptimePct: form.uptimePct ? Number(form.uptimePct) : undefined,
            }
          : undefined;
      const pricing = {
        model: form.pricingModel,
        priceWei: adiToWei(form.priceAdi || '0'),
        currency: 'ADI' as const,
      };

      if (modal === 'create') {
        const body = {
          agentDid: form.agentDid,
          title: form.title,
          description: form.description || undefined,
          capabilityScope: form.capabilityScope,
          pricing,
          ibctPolicyId: form.ibctPolicyId || undefined,
          mcpToolId: form.mcpToolId || undefined,
          sla,
        };
        await apiFetch('/marketplace/listings', {
          method: 'POST',
          headers: { 'Idempotency-Key': uuid() },
          body: JSON.stringify(body),
        });
        setToast({ kind: 'success', msg: t('myListings.toasts.created') });
      } else if (modal === 'edit' && editing) {
        const body = {
          ifVersion: editing.version,
          title: form.title,
          description: form.description || undefined,
          pricing,
          sla,
        };
        await apiFetch(`/marketplace/listings/${encodePathSegment(editing.id)}`, {
          method: 'PUT',
          body: JSON.stringify(body),
        });
        setToast({ kind: 'success', msg: t('myListings.toasts.updated') });
      }
      close();
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'save_failed';
      if (msg.toLowerCase().includes('version_conflict')) {
        setFormError(t('myListings.errors.versionConflict'));
      } else {
        setFormError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitPause = async () => {
    if (!editing) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await apiFetch(`/marketplace/listings/${encodePathSegment(editing.id)}/pause`, {
        method: 'POST',
        body: JSON.stringify({ reason: pauseReason || 'manual_pause' }),
      });
      setToast({ kind: 'success', msg: t('myListings.toasts.paused') });
      close();
      await refresh();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'pause_failed');
    } finally {
      setSubmitting(false);
    }
  };

  const submitResume = async (l: MarketplaceListing) => {
    try {
      await apiFetch(`/marketplace/listings/${encodePathSegment(l.id)}/resume`, {
        method: 'POST', body: JSON.stringify({}),
      });
      setToast({ kind: 'success', msg: t('myListings.toasts.resumed') });
      await refresh();
    } catch (e) {
      setToast({ kind: 'error', msg: e instanceof Error ? e.message : 'resume_failed' });
    }
  };

  const submitDelete = async () => {
    if (!editing) return;
    setSubmitting(true);
    setFormError(null);
    try {
      await apiFetch(`/marketplace/listings/${encodePathSegment(editing.id)}`, { method: 'DELETE' });
      setToast({ kind: 'success', msg: t('myListings.toasts.deleted') });
      close();
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'delete_failed';
      if (msg.toLowerCase().includes('active_escrows')) {
        setFormError(t('myListings.errors.activeEscrows'));
      } else {
        setFormError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('myListings.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('myListings.subtitle')}</p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <span className="material-symbols-outlined mr-1" style={{ fontSize: 16 }}>add</span>
          {t('myListings.create')}
        </button>
      </div>

      {toast && (
        <div className={`rounded-lg border px-4 py-2 text-xs font-semibold ${toast.kind === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-red-200 bg-red-50 text-red-700'}`}>
          {toast.msg}
        </div>
      )}

      <div className="rounded-xl border border-border-subtle bg-white shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-subtle bg-slate-50/50">
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.title')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.capability')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.pricing')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.status')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.version')}</th>
              <th className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('myListings.table.updated')}</th>
              <th className="px-5 py-2.5 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-text-faint">{t('common:loading')}</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-red-500">{error}</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-text-faint">{t('myListings.empty')}</td></tr>
            ) : items.map((l) => (
              <tr key={l.id} className="border-b border-border-subtle hover:bg-slate-50/60">
                <td className="px-5 py-3">
                  <p className="text-sm font-bold text-text-main">{l.title}</p>
                  <p className="text-[10px] text-text-faint truncate max-w-xs">{l.id}</p>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-text-muted">{l.capabilityScope}</td>
                <td className="px-5 py-3">
                  <p className="text-sm font-bold text-text-main">{weiToAdi(l.pricing.priceWei)} ADI</p>
                  <p className="text-[10px] text-text-faint">{l.pricing.model}</p>
                </td>
                <td className="px-5 py-3">
                  <Badge variant={listingStatusBadge(l.status)}>{l.status}</Badge>
                </td>
                <td className="px-5 py-3 text-xs text-text-muted">v{l.version}</td>
                <td className="px-5 py-3 text-xs text-text-muted">{new Date(l.updatedAt).toLocaleDateString()}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => openEdit(l)} title={t('myListings.actions.edit')}
                      className="rounded p-1 text-text-faint hover:bg-slate-100 hover:text-text-main">
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>edit</span>
                    </button>
                    {l.status === 'active' ? (
                      <button onClick={() => openPause(l)} title={t('myListings.actions.pause')}
                        className="rounded p-1 text-text-faint hover:bg-amber-50 hover:text-amber-600">
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>pause</span>
                      </button>
                    ) : l.status === 'paused' ? (
                      <button onClick={() => submitResume(l)} title={t('myListings.actions.resume')}
                        className="rounded p-1 text-text-faint hover:bg-emerald-50 hover:text-emerald-600">
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>play_arrow</span>
                      </button>
                    ) : null}
                    <button onClick={() => openDelete(l)} title={t('myListings.actions.delete')}
                      className="rounded p-1 text-text-faint hover:bg-red-50 hover:text-red-600">
                      <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit modal */}
      <Modal
        open={modal === 'create' || modal === 'edit'}
        onClose={close}
        wide
        title={modal === 'edit' ? t('myListings.form.editTitle') : t('myListings.form.createTitle')}
      >
        <div className="space-y-4">
          {modal === 'create' && (
            <FormField
              label={t('myListings.form.agentDid')}
              as="select"
              value={form.agentDid}
              onChange={(e) => setForm({ ...form, agentDid: e.target.value })}
            >
              <option value="">{agentOptions.length === 0 ? 'No agents found — deploy one first' : 'Select an agent…'}</option>
              {agentOptions.map((a) => (
                <option key={a.did} value={a.did}>{a.name} — {a.did.split(':').pop()?.slice(0, 20)}</option>
              ))}
            </FormField>
          )}
          <FormField
            label={t('myListings.form.title')}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <FormField
            label={t('myListings.form.description')}
            as="textarea"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {modal === 'create' && (
              <FormField
                label={t('myListings.form.capabilityScope')}
                placeholder={t('myListings.form.capabilityScopePlaceholder')}
                value={form.capabilityScope}
                onChange={(e) => setForm({ ...form, capabilityScope: e.target.value })}
              />
            )}
            <FormField
              label={t('myListings.form.pricingModel')}
              as="select"
              value={form.pricingModel}
              onChange={(e) => setForm({ ...form, pricingModel: e.target.value as PricingModel })}
            >
              <option value="per_call">{t('browse.pricingModels.per_call')}</option>
              <option value="per_hour">{t('browse.pricingModels.per_hour')}</option>
              <option value="flat_fee">{t('browse.pricingModels.flat_fee')}</option>
            </FormField>
            <FormField
              label={t('myListings.form.priceWei')}
              hint={t('myListings.form.priceWeiHint')}
              value={form.priceAdi}
              onChange={(e) => setForm({ ...form, priceAdi: e.target.value })}
              placeholder="0.1"
            />
          </div>
          {modal === 'create' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField
                label={`${t('myListings.form.ibctPolicyId')} *`}
                as="select"
                value={form.ibctPolicyId}
                onChange={(e) => setForm({ ...form, ibctPolicyId: e.target.value })}
              >
                <option value="">{policyOptions.length === 0 ? 'No policies found — create one first' : 'Select a policy…'}</option>
                {policyOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </FormField>
              <FormField
                label={t('myListings.form.mcpToolId')}
                as="select"
                value={form.mcpToolId}
                onChange={(e) => setForm({ ...form, mcpToolId: e.target.value })}
              >
                <option value="">
                  {!form.agentDid ? 'Select an agent first' : toolsLoading ? 'Loading…' : toolOptions.length === 0 ? 'No tools registered' : 'Select a tool (optional)'}
                </option>
                {toolOptions.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </FormField>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField
              label={t('myListings.form.latencyP95Ms')}
              type="number"
              value={form.latencyP95Ms}
              onChange={(e) => setForm({ ...form, latencyP95Ms: e.target.value })}
            />
            <FormField
              label={t('myListings.form.uptimePct')}
              type="number"
              value={form.uptimePct}
              onChange={(e) => setForm({ ...form, uptimePct: e.target.value })}
            />
          </div>

          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={close} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('myListings.form.cancel')}
            </button>
            <button onClick={submitForm} disabled={submitting} className="btn-primary text-xs disabled:opacity-50">
              {submitting ? t('myListings.form.submitting') : t('myListings.form.submit')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Pause modal */}
      <Modal open={modal === 'pause'} onClose={close} title={t('myListings.pauseModal.title')}>
        <div className="space-y-4">
          <FormField
            label={t('myListings.pauseModal.reason')}
            placeholder={t('myListings.pauseModal.reasonPlaceholder')}
            value={pauseReason}
            onChange={(e) => setPauseReason(e.target.value)}
          />
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={close} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button onClick={submitPause} disabled={submitting} className="btn-primary text-xs disabled:opacity-50">
              {submitting ? t('common:saving') : t('myListings.pauseModal.confirm')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete modal */}
      <Modal open={modal === 'delete'} onClose={close} title={t('myListings.deleteModal.title')}>
        <div className="space-y-4">
          <p className="text-sm text-text-muted">{t('myListings.deleteModal.body')}</p>
          {formError && <p className="text-xs font-semibold text-red-500">{formError}</p>}
          <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
            <button onClick={close} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-text-muted hover:bg-slate-50">
              {t('common:cancel')}
            </button>
            <button
              onClick={submitDelete}
              disabled={submitting}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {submitting ? t('common:saving') : t('myListings.deleteModal.confirm')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
